import React, {useCallback, useState} from 'react'
import {FaExclamationCircle} from 'react-icons/fa'
import {MdWarning} from 'react-icons/md'
import Button from '@components/V3/Button'
import Modal from '@components/V3/Modal'
import Notice from '@components/V3/Notice'
import useMessage from '@hooks/useMessage'
import {useContactModalContext} from '@page-components/TrackV2/components/ContactModalContext'
import useOrder from '@page-components/TrackV2/hooks/useOrder.main'
import useStatus from '@page-components/TrackV2/hooks/useStatus'
import {useMutate} from 'apollo-hooks'
import gql from 'graphql-tag'
import {useTranslation} from 'next-i18next'

import useCancelInfo from './hooks/useCancelInfo.main'

function CancelModal({showModal, setShowModal}) {
  const [loading, setLoading] = useState(false)
  const {t} = useTranslation('tracking', {keyPrefix: 'notConfirmedInTime.cancelModal'})
  const mutate = useMutate()
  const showMessage = useMessage()
  const {order} = useOrder()

  const onConfirm = useCallback(async () => {
    setLoading(true)
    try {
      await mutate({
        mutation: gql`
          mutation TrackingV2CancelOrder($orderId: ID!) {
            userCancelPendingOrder(orderId: $orderId) {
              _id
              orderStatus
            }
          }
        `,
        variables: {orderId: order._id}
      })
      showMessage(t('canceled'))
    } catch (error) {
      showMessage(`Tu pedido no pudo ser cancelado: ${error.message}`)
    }
    setLoading(false)
    setShowModal(false)
  }, [order._id])

  return (
    <Modal isOpen={showModal} onClose={() => setShowModal(false)} forceJustoFont showClose={false}>
      <div className="flex flex-col divide-y gap-4 divide-gray-100 text-sm items-center">
        <div className="text-center pt-4">
          <MdWarning size={24} color="#FB0024" />
          <h2 className="text-lg font-bold">{t('title')}</h2>
        </div>
        <p className="px-2 pt-4">{t('explanation')}</p>
        <div className="flex space-x-4 w-full pt-4">
          <Button disabled={loading} loading={loading} full big danger onClick={onConfirm}>
            {loading ? '' : t('yes')}
          </Button>
          <Button disabled={loading} full big onClick={() => setShowModal(false)}>
            {t('no')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function NotConfirmedInTimeInner() {
  const {canUserCancelPendingOrder, cancelationCustomMessage} = useCancelInfo()
  const {setOpen} = useContactModalContext()
  const [showModal, setShowModal] = useState(false)
  const {t} = useTranslation('tracking', {keyPrefix: 'notConfirmedInTime'})

  if (!canUserCancelPendingOrder) return null

  return (
    <div className="flex flex-col gap-4">
      <CancelModal showModal={showModal} setShowModal={setShowModal} />
      <Notice level="warning" className="!rounded-lg">
        <div className="flex space-x-4 items-center">
          <FaExclamationCircle size={18} />
          <div className="flex-1">
            <p>{cancelationCustomMessage ? cancelationCustomMessage : t('warning')}</p>
          </div>
        </div>
      </Notice>
      <Button primary full onClick={() => setOpen(true)}>
        {t('contact')}
      </Button>
      <Button full onClick={() => setShowModal(true)}>
        {t('cancel')}
      </Button>
    </div>
  )
}

export default function NotConfirmedInTime() {
  const status = useStatus()
  const {order} = useOrder()

  if (status !== 'confirmation') return null
  if (order.isScheduled) return null

  return <NotConfirmedInTimeInner />
}
